import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import useFormState from '../../hooks/useFormState';

import Button from '../Button';
import Input from '../Input';
import Textarea from '../Textarea';
import Stack from '../Stack';
import Checkbox from '../Checkbox';

export type ContributeAnnotationFormState = {
  name: string;
  email: string;
  annotation: string;
  publishName: boolean;
};

export type ContributeAnnotationFormProps = {
  onSubmit: (state: ContributeAnnotationFormState) => void;
};

const ContributeAnnotationForm = ({ onSubmit }: ContributeAnnotationFormProps) => {
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation('common');

  const {
    handleSubmit,
    updateValue,
    resetError,
    publishName,
    email,
    annotation,
    name,
    error,
    setError
  } = useFormState({
    initialState: {
      email: '',
      name: '',
      annotation: '',
      copyright: false,
      publishName: false
    },
    validate: () => {
      let valid = true;

      if (name === '') {
        setError({ name: true });
        valid = false;
      }

      if (email === '') {
        setError({ email: true });
        valid = false;
      }

      if (annotation === '') {
        setError({ annotation: true });
        valid = false;
      }

      return valid;
    },
    onSubmit: async () => {
      setSubmitting(true);

      try {
        await onSubmit({ name, publishName, email, annotation });
        setSubmitting(false);
      } catch (error) {
        setSubmitting(false);
        return console.log(error);
      }
    }
  });

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack gap="m">
        <Stack gap="s">
          <Input
            id="contribute-annotation-email"
            type="email"
            onChange={e => updateValue('email', e.target.value)}
            disabled={submitting}
            onFocus={resetError}
            value={email}
            error={error.email}
            label={t('form.email.label')}
            placeholder={t('form.email.placeholder')}
            errorMessage={t('form.email.error')}
            autocomplete="email"
            required
          />

          <Input
            type="text"
            onChange={e => updateValue('name', e.target.value)}
            disabled={submitting}
            onFocus={resetError}
            value={name}
            error={error.name}
            autocomplete="name"
            required
            id="contribute-annotation-name"
            label={t('form.name.label')}
            placeholder={t('form.name.placeholder')}
            errorMessage={t('form.name.error')}
          />

          <Checkbox
            onChange={e => updateValue('publishName', e.target.checked)}
            onFocus={resetError}
            disabled={submitting}
            checked={publishName}
            error={error.publishName}
            id="contribute-annotation-publish-name"
            label={t('form.publishName.label')}
          />

          <Textarea
            onChange={e => updateValue('annotation', e.target.value)}
            disabled={submitting}
            onFocus={resetError}
            value={annotation}
            error={error.annotation}
            required
            rows={6}
            id="contribute-annotation-annotation"
            label={t('form.annotation.label')}
            placeholder={t('form.annotation.placeholder')}
            errorMessage={t('form.errors.required')}
          />
        </Stack>

        <Button type="submit" disabled={submitting} label={t('form.labels.submit')} />
      </Stack>
    </form>
  );
};

export default ContributeAnnotationForm;
